import React from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Calendar, MapPin, Ticket } from "lucide-react";
import EventBookingButton from "@/components/EventBookingButton";

const CAT_LABELS = {
  stage: "Stages",
  soiree: "Soirées",
  spectacle: "Spectacles",
  enfants: "Événements enfants",
  heelevents: "Événements Heel'Events",
  compagnie: "Compagnie",
};

const STATUS_LABELS = { open: "Ouvert", complet: "Complet", termine: "Terminé", "terminé": "Terminé" };

// N'affiche publiquement que les événements jusqu'à cette date (les suivants restent gérés dans l'admin)
const VISIBLE_UNTIL = "2026-09-30";
// Le bouton Réserver s'affiche automatiquement dès qu'un lien HelloAsso est renseigné sur l'événement (via l'admin)

export default function Evenements() {
  const [events, setEvents] = React.useState([]);
  const [cat, setCat] = React.useState("all");
  React.useEffect(() => { http.get("/events").then(r => setEvents(r.data)).catch(() => setEvents([])); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const visible = events.filter(e => cat === "all" || e.category === cat);
  const upcoming = visible.filter(e => e.date >= today && e.date <= VISIBLE_UNTIL).sort((a, b) => a.date.localeCompare(b.date));
  const future = visible.filter(e => e.date > VISIBLE_UNTIL);
  const past = visible.filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <p className="he-overline text-neutral-500">Heel’Events</p>
      <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter mt-3">Agenda</h1>
      <p className="mt-4 text-neutral-600 font-body max-w-2xl">
        Stages, soirées, spectacles : tous les rendez-vous de l’association, avec réservation en ligne.
      </p>

      {/* Filtres catégories */}
      <div data-testid="events-filters" className="mt-8 flex flex-wrap gap-2">
        {[["all", "Tout voir"], ...Object.entries(CAT_LABELS)].map(([v, l]) => (
          <button
            key={v}
            data-testid={`events-filter-${v}`}
            onClick={() => setCat(v)}
            className={`rounded-full px-4 py-2 text-xs border transition-colors ${
              cat === v ? "bg-[var(--he-red)] text-white border-[var(--he-red)]" : "border-black/15 hover:border-[var(--he-red)]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-heading text-3xl font-bold border-b border-black/10 pb-3">À venir</h2>
        {upcoming.length === 0 ? (
          <p className="mt-6 text-neutral-500 text-sm">Aucun événement à venir pour le moment. Revenez bientôt !</p>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {upcoming.map(EventCard)}
          </div>
        )}
      </section>

      {future.length > 0 && (
        <p data-testid="events-coming-soon" className="mt-10 text-sm text-neutral-500 border border-dashed border-black/15 rounded-xl px-5 py-4">
          Et ce n'est pas tout — <strong>{future.length} événement{future.length > 1 ? "s" : ""} d'octobre à décembre 2026 et de la saison 2027</strong> (soirées SBKK, Kids Party, Cabaret, Gala, Afro Lat'Fest…) seront dévoilés prochainement. Restez connectés !
        </p>
      )}

      {past.length > 0 && (
        <section className="mt-20">
          <h2 className="font-heading text-3xl font-bold border-b border-black/10 pb-3">Nos précédents événements</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 opacity-80">
            {past.map(EventCard)}
          </div>
        </section>
      )}
    </div>
  );
}

function EventCard(e) {
  const status = STATUS_LABELS[e.status] || "Ouvert";
  const reservable = (e.status === "open" || e.status === "ouvert") && (e.end_date || e.date) >= new Date().toISOString().slice(0, 10);
  return (
    <article key={e.id} data-testid="event-card" className="border border-black/10 rounded-2xl overflow-hidden hover:border-[var(--he-red)] transition-colors flex flex-col">
      {e.image ? (
        <div className="relative aspect-[16/10] bg-neutral-100 overflow-hidden">
          <img src={e.image} alt={e.name} className="w-full h-full object-cover" />
          <span data-testid="event-status" className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${status === "Ouvert" ? "bg-white text-black" : status === "Complet" ? "bg-[var(--he-red)] text-white" : "bg-black/70 text-white"}`}>
            {status}
          </span>
        </div>
      ) : (
        <div className="relative aspect-[16/10] bg-neutral-100 flex items-center justify-center text-neutral-300 font-heading text-6xl font-black">
          ·
          <span data-testid="event-status" className={`absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${status === "Ouvert" ? "bg-black text-white" : status === "Complet" ? "bg-[var(--he-red)] text-white" : "bg-black/40 text-white"}`}>
            {status}
          </span>
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <div className="he-overline text-neutral-500">{CAT_LABELS[e.category] || e.category}</div>
        <h3 className="mt-2 font-heading text-xl font-bold">{e.name}</h3>
        <div className="mt-3 text-sm text-neutral-600 space-y-1">
          <p className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--he-red)]" /> {e.date}{e.time ? ` · ${e.time}` : ""}</p>
          {e.location ? <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--he-red)]" /> {e.location}</p> : null}
          {e.price ? <p className="text-xs text-neutral-500">{e.price}</p> : null}
        </div>
        {e.description ? <p className="mt-3 text-sm text-neutral-600 font-body line-clamp-3">{e.description}</p> : null}

        <div className="mt-5 pt-4 border-t border-black/5 mt-auto">
          {reservable && e.widget_url ? (
            <EventBookingButton url={e.widget_url} label={/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver ma place"} />
          ) : reservable && e.booking_url ? (
            <a
              href={e.booking_url}
              target="_blank"
              rel="noreferrer"
              data-testid="event-booking-btn"
              className="btn-red rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-2"
            >
              <Ticket className="w-4 h-4" /> {/portes ouvertes/i.test(e.name || "") ? "Participer" : "Réserver"}
            </a>
          ) : reservable ? (
            <span data-testid="event-booking-btn" className="text-xs text-neutral-400 uppercase tracking-widest font-bold">Billetterie bientôt disponible</span>
          ) : (
            <span className="text-xs text-neutral-400 uppercase tracking-widest font-bold">{status}</span>
          )}
        </div>
      </div>
    </article>
  );
}
